/**
 * Simulated iOS status bar for showcase PHONE mode only (spec §C).
 * Plain-DOM fixed overlay (not React): injected by bootShowcase after
 * render, zero coupling to packages/core (which is shared with native —
 * hard no-OTA constraint). Layout is PROPORTIONAL (vw units) because the
 * live preview iframe is fluid below 390px while the worker render is
 * exactly 390px — fixed px offsets would collide with the frame art's
 * island on narrow embeds. Time is fixed at 9:41 (deterministic renders).
 * The row sits on the frame art's island centreline (manifest-derived); the
 * brand-colored header chrome painted behind the --safe-area-top strip is
 * what it reads against.
 */
import geometry from 'webeatery-device-frames/geometry.json'
import { SHOWCASE_FORCED_HEX } from './fixtures'

// Single source (spec §A): the frames manifest owns these numbers. A test
// asserts the design-tokens.css --safe-area-top token still matches the
// manifest's safeAreaTop, which is what reserves the strip this bar sits in.
const ISLAND = geometry.phone.island

/** WCAG relative luminance of a #rrggbb hex. */
export function relativeLuminance(hex: string): number {
  const n = hex.replace('#', '')
  const channel = (i: number): number => {
    const c = parseInt(n.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
}

/**
 * Glyph color for the simulated status bar. The hex passed in must be the color
 * actually painted BEHIND the glyphs — the brand-derived header chrome in the
 * --safe-area-top strip. Since round 3 that is SHOWCASE_FORCED_HEX for every
 * lead, so the fallback is the same constant rather than a stale brand default.
 */
export function statusBarForeground(brandHex: string | null): string {
  const hex = /^#[0-9a-fA-F]{6}$/.test(brandHex ?? '') ? (brandHex as string) : SHOWCASE_FORCED_HEX
  return relativeLuminance(hex) > 0.5 ? '#0A0A0A' : '#FFFFFF'
}

// Simple recognizable glyphs; stroke/fill use currentColor.
const SIGNAL_SVG =
  '<svg width="1em" height="0.75em" viewBox="0 0 18 12" fill="currentColor" aria-hidden="true">' +
  '<rect x="0" y="8" width="3" height="4" rx="1"/><rect x="5" y="5.5" width="3" height="6.5" rx="1"/>' +
  '<rect x="10" y="3" width="3" height="9" rx="1"/><rect x="15" y="0.5" width="3" height="11.5" rx="1"/></svg>'
const WIFI_SVG =
  '<svg width="1em" height="0.75em" viewBox="0 0 16 12" fill="currentColor" aria-hidden="true">' +
  '<path d="M8 10.8a1.4 1.4 0 100 2.8 1.4 1.4 0 000-2.8z" transform="translate(0,-2.2)"/>' +
  '<path d="M8 6.7c1.5 0 2.9.6 3.9 1.6l-1.3 1.3A3.9 3.9 0 008 8.5c-1 0-2 .4-2.6 1.1L4.1 8.3c1-1 2.4-1.6 3.9-1.6z"/>' +
  '<path d="M8 3.2c2.5 0 4.7 1 6.3 2.6l-1.3 1.3A7.1 7.1 0 008 5c-1.9 0-3.7.8-5 2.1L1.7 5.8A8.9 8.9 0 018 3.2z"/></svg>'
const BATTERY_SVG =
  '<svg width="1.6em" height="0.75em" viewBox="0 0 25 12" aria-hidden="true">' +
  '<rect x="0.5" y="0.5" width="21" height="11" rx="3" fill="none" stroke="currentColor" opacity="0.4"/>' +
  '<rect x="2" y="2" width="18" height="8" rx="1.8" fill="currentColor"/>' +
  '<path d="M23 4v4c1.1-.2 1.8-1 1.8-2S24.1 4.2 23 4z" fill="currentColor" opacity="0.4"/></svg>'

/**
 * The island's vertical centre, as a % of viewport height, straight from the
 * frame manifest (measured off the frame art itself). iOS puts the time and
 * indicators on this line — bottom-anchoring them instead makes the row hug
 * the app header and read as "not a real status bar", which is exactly what
 * the first cut did.
 */
export const ISLAND_CENTRE_PCT = ISLAND.topPct + ISLAND.heightPct / 2

export function buildShowcaseStatusBar(brandHex: string | null): HTMLElement {
  const el = document.createElement('div')
  el.dataset.showcaseStatusbar = '1'
  el.setAttribute('aria-hidden', 'true')
  const fg = statusBarForeground(brandHex)
  Object.assign(el.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    // Height is twice the island's centreline so `align-items: center` lands
    // the row exactly on it (≈31px at the 390×844 render viewport).
    height: `${(ISLAND_CENTRE_PCT * 2).toFixed(3)}vh`,
    zIndex: '9999',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Side gutters clear the island (which spans ~34.6%–65.4% of the width).
    padding: '0 7.5%',
    color: fg,
    // 15px at the worker's exact 390px viewport; scales down on narrower live iframes
    fontSize: 'min(15px, 3.85vw)',
    fontFamily: "-apple-system, 'SF Pro Text', system-ui, sans-serif",
    fontWeight: '600',
    boxSizing: 'border-box',
  } satisfies Partial<CSSStyleDeclaration>)
  const time = document.createElement('span')
  time.textContent = '9:41'
  // Both children centre on the same line: the glyphs are shorter than the
  // time's line box, so any edge-alignment (flex-end especially) leaves the
  // battery sitting several px below the clock.
  time.style.display = 'inline-flex'
  time.style.alignItems = 'center'
  time.style.lineHeight = '1'
  const icons = document.createElement('span')
  icons.style.display = 'inline-flex'
  icons.style.alignItems = 'center'
  icons.style.gap = '0.35em'
  icons.innerHTML = `${SIGNAL_SVG}${WIFI_SVG}${BATTERY_SVG}`
  el.append(time, icons)
  return el
}

/** Mount once per document; phone showcase only. */
export function injectShowcaseStatusBar(brandHex: string | null, device: 'phone' | 'browser' | null): void {
  if (device !== 'phone') return
  if (document.querySelector('[data-showcase-statusbar]')) return
  document.body.appendChild(buildShowcaseStatusBar(brandHex))
}
